import { useRef, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { ROUTES, INVITE_ONLY_ROUTES,AI_CONFIG_KEYS} from 'shared-ui/utils/constants'
import { useFetchInviteAPI, useConfiguration } from 'shared-ui/hooks'
import { Loader } from 'shared-ui/components'
import { getGuestUser } from 'shared-ui/utils/localStorage.js'
import { isPreviewMode } from 'shared-ui/utils/previewMode'
import {AUTH_TOOL_ROUTES} from 'shared-ui/utils/constants'
import { setConfiguration, setAIConfig} from 'shared-ui/redux/reducers/configReducer'


export const InviteRouteGuard = ({
  children
}) => {

  const DEVICE_ALREADY_INVITED_STATUS_CODE = 'USS06'
  const location = useLocation()
  const dispatch = useDispatch()
  const currentPath = location.pathname
  const accessToken = localStorage.getItem('access')
  const isGuestUser = getGuestUser() === "true"

  const { pathname, search, state } = location
  const params = new URLSearchParams(search)

  const isAndroidPaymentFlow =
    (pathname === ROUTES.payment || pathname === ROUTES.paymentFailed) &&
    (params.get('source') === 'ANDROID' || state?.source === 'ANDROID')

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
  const { data: inviteData, isLoading, isError, isPending } = useFetchInviteAPI()
  const { data: configurationData } = useConfiguration()

  const extractAIConfig = (data) => {
    const config = {};
    data?.forEach((item) => {
      if (!AI_CONFIG_KEYS.includes(item.config_key)) return;

      try {
        const parsedValue = JSON.parse(item.value);

        config[item.config_key] = {
          enabled: parsedValue.enabled ?? false,
          supported_tools: parsedValue.supported_tools ?? [],
        };
      } catch {
        const value = item.value || '';

        const enabledMatch = value.match(/"enabled"\s*:\s*(true|false)/);
        const toolsMatch = value.match(
          /supported_tools\s*\[([^\]]*)\]/
        );

        const supportedTools = toolsMatch
          ? toolsMatch[1]
            .match(/"([^"]+)"/g)
            ?.map((tool) => tool.replace(/"/g, '')) || []
          : [];

        config[item.config_key] = {
          enabled: enabledMatch?.[1] === 'true',
          supported_tools: supportedTools,
        };
      }
    });

    return config;
  };
  useEffect(() => {
  if (configurationData) {
    dispatch(setConfiguration(configurationData));
    const aiConfig = extractAIConfig(configurationData.data);
    dispatch(setAIConfig(aiConfig));
    localStorage.setItem('appConfiguration', typeof configurationData === 'string' ? configurationData : JSON.stringify(configurationData));
  }
}, [configurationData, dispatch]);

  if (isAndroidPaymentFlow) {
    return children
  }

  // The CMS preview renders this site in a cross-origin iframe, so it has no
  // deviceId and no session. Without this bypass the guard below would redirect
  // to the waitlist and the admin would never see the page being previewed.
  if (isPreviewMode()) {
    return children
  }

  const checkInviteMessage = () => {
    if (inviteData?.success === true) {
      return inviteData.status_code
    }
    if (inviteData?.success === false) {
      return inviteData?.error?.message
    }
    return null
  }

  const inviteStatusCode = checkInviteMessage()
  const inviteEnabled = DEVICE_ALREADY_INVITED_STATUS_CODE === inviteStatusCode

  const deviceId = typeof window !== 'undefined' ? localStorage.getItem('deviceId') : null;
  const isNewDeviceRef = useRef(!deviceId);

  // If deviceId is missing, the device registration is running in the background.
  // We know it's a new device, so it's definitely not invited yet. We can show the Waitlist UI immediately.
  if (!deviceId) {
    isNewDeviceRef.current = true;
    if (!INVITE_ONLY_ROUTES.includes(currentPath)) {
      return <Navigate to={ROUTES.waitListLanding} replace />
    }
    return children
  }

  // In React Query v5, disabled queries have isLoading=false and isPending=true.
  // We check isPending to properly show the Loader instead of a blank screen when fetching.
  const isQueryLoading = isLoading || isPending;

  if (isQueryLoading || !inviteStatusCode) {
    if (isNewDeviceRef.current && currentPath === ROUTES.waitListLanding) {
      return children;
    }
    return <Loader show={true} />
  }

  if (isError && !accessToken && currentPath === ROUTES.waitListLanding && !isGuestUser) {
    return <Navigate to={ROUTES.waitListLanding} replace />
  }

  if (!inviteEnabled && !isGuestUser && !accessToken) {
    if (!INVITE_ONLY_ROUTES.includes(currentPath)) {
      return <Navigate to={ROUTES.waitListLanding} replace />
    }
  }

   if (inviteEnabled && !isAuthenticated && !isGuestUser && AUTH_TOOL_ROUTES?.includes(currentPath)){
    return <Navigate to={ROUTES.login} replace />
  }

  if (inviteEnabled && currentPath === ROUTES.waitListLanding && !isAuthenticated ) {
    return <Navigate to={ROUTES.waitListWelcome} replace />
  }

  return children
}
